import { Check, Copy } from "lucide-react";
import { type ReactElement, useCallback, useState } from "react";

import { cn } from "@/lib/utils";

import type { Citation } from "../utils/citations";
import { copyToClipboard } from "../utils/clipboard";

interface CitationReferenceProps {
  citation: Citation;
}

export function CitationReference({ citation }: CitationReferenceProps): ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((): void => {
    void copyToClipboard(citation.raw).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  }, [citation.raw]);

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        "rounded border border-border bg-muted/50 px-1.5 py-0.5",
        "text-xs font-mono"
      )}
      data-testid="citation-reference"
    >
      <span className="text-muted-foreground">{citation.path}</span>
      <span className="text-muted-foreground/60">@{citation.sha.slice(0, 7)}</span>
      <button
        onClick={handleCopy}
        className={cn(
          "ml-0.5 inline-flex items-center justify-center",
          "rounded p-0.5 hover:bg-muted",
          "text-muted-foreground hover:text-foreground",
          "transition-colors"
        )}
        aria-label="Copy reference"
        data-testid="citation-copy-button"
        title="Copy reference"
        type="button"
      >
        {copied ? (
          <span data-testid="citation-copied">
            <Check className="h-3 w-3" />
          </span>
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </button>
    </span>
  );
}
